import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { StatusPill } from '@/components/Status';
import { statusInk, type StatusTone } from '@/lib/status';
import { getReceivingBalance, type ReceivingBalance } from '@/api/orders';
import { ProductLink } from './ProductLink';
import { blockerHelp } from './blockers';
import { DISCREPANCY_KIND_LABELS, money, unitCost } from './lib';
import { WorkNotice } from './workLib';

/** A credit's state, as in the Decide view: landed is done, requested is with
 *  the vendor, open still needs asking. */
const RESOLUTION_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  requested: 'info',
  resolved: 'success',
};

/**
 * The computed balance for one receiving: blockers, the count-discrepancy list
 * (warehouse → supplier), the distribution math, and the literal cost-edit
 * list. Everything shown here is derived server-side from the three documents —
 * nothing is an agent's claim.
 */
export function BalanceReport({ receiptId, currency }: { receiptId: string; currency: string }) {
  const [balance, setBalance] = useState<ReceivingBalance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllEdits, setShowAllEdits] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReceivingBalance({ receiptId })
      .then((b) => { if (!cancelled) setBalance(b); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [receiptId]);

  if (error) {
    return <WorkNotice tone="danger">{error}</WorkNotice>;
  }
  if (!balance) {
    return (
      <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Computing balance…
      </div>
    );
  }

  const { distribution: d, totals: t } = balance;
  const gapOk = t.distributionGap === 0;
  const edits = showAllEdits ? balance.costEdits : balance.costEdits.slice(0, 12);

  return (
    <div className="space-y-3 text-xs">
      {/* Blockers come first — nothing below is trustworthy until they're clear.
          Each is a typed code with a short clause; what unblocks it is the tooltip. */}
      {balance.blockers.length > 0 && (
        <WorkNotice tone="warning">
          {balance.blockers.map((b) => (
            <div key={b.code}>
              <Hint content={blockerHelp(b)}><span>{b.text}</span></Hint>
            </div>
          ))}
        </WorkNotice>
      )}

      {/* Distribution: how the invoice total spreads across the lines */}
      <div className="rounded-md border border-border/60 bg-background/60 p-2">
        <div className="mb-1 font-medium">Distribution</div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground">
          <span>Goods {money(d.productSubtotal, currency)}<span className="opacity-60"> ({d.subtotalBasis})</span></span>
          {d.freight !== 0 && <span>Freight {money(d.freight, currency)}</span>}
          {d.duties !== 0 && <span>Duties {money(d.duties, currency)}</span>}
          {d.carrierCharges != null && d.carrierCharges !== 0 && (
            <span>Carrier duties {money(d.carrierCharges, currency)}</span>
          )}
          {d.otherFees !== 0 && <span>Fees {money(d.otherFees, currency)}</span>}
          {d.discount !== 0 && <span>Discount −{money(d.discount, currency)}</span>}
          <span className="text-foreground">Paid {money(d.invoiceTotal, currency)}</span>
          {d.factor != null && (
            <span>Factor <span className="text-foreground tabular-nums">{d.factor.toFixed(6)}</span><span className="opacity-60"> ({d.factorBasis})</span></span>
          )}
        </div>
        {d.chargeCheck && !d.chargeCheck.agrees && (
          <div className={cn('mt-1', statusInk('warning'))}>
            Charge check: itemised {money(d.chargeCheck.itemisedCharges, currency)} vs implied {money(d.chargeCheck.impliedCharges, currency)} — off by {money(d.chargeCheck.gap, currency)}.
          </div>
        )}
        {/* An invoice that doesn't extend to its own stated subtotal has a
            capture error in its lines — worth naming separately from the
            charge check, which only tests subtotal + charges = total. */}
        {d.lineCheck && !d.lineCheck.agrees && (
          <div className={cn('mt-1', statusInk('warning'))}>
            {d.lineCheck.perInvoice.filter((c) => !c.agrees).map((c) => (
              <div key={c.invoiceNo ?? 'x'}>
                Invoice {c.invoiceNo ?? '(no number)'} doesn't reconcile to itself: products extend to{' '}
                {money(c.lineExtSum, currency)} vs stated subtotal {money(c.statedSubtotal, currency)} — off by {money(c.gap, currency)}.
              </div>
            ))}
          </div>
        )}
        {/* 5dp costs x integer quantities cannot always hit the invoice total to
            the penny. The spare cents are settled onto the lines that lost the
            most to rounding; say which, so the target costs are never a mystery. */}
        {d.residual && (
          <div className="mt-1 opacity-70">
            {d.residual.lines > 0 && (
              <>
                Rounding: {money(d.residual.cents, currency)} settled onto {d.residual.lines} product
                {d.residual.lines === 1 ? '' : 's'} (
                {d.residual.appliedTo.map((a, i) => (
                  <span key={a.pid ?? a.itemnumber ?? i}>
                    {i > 0 && ', '}
                    <ProductLink pid={a.pid}>{a.itemnumber || a.desc || a.pid}</ProductLink>
                  </span>
                ))}
                ) so the receiving ties exactly.
              </>
            )}
            {/* The stored header total rounds the sum, not each line, so it can sit a
                cent out even when every line ties. Sub-cent shifts inside lines'
                existing cents fix that without moving any rounded extension. */}
            {d.residual.headerAdjustment && (
              <div>
                Header total: {d.residual.headerAdjustment.lines} product
                {d.residual.headerAdjustment.lines === 1 ? '' : 's'} nudged within their existing cent so the
                stored total ties too{d.residual.headerAdjustment.settled ? '' : ' (could not fully settle)'}.
              </div>
            )}
          </div>
        )}
        {/* Several invoices on one receiving can price at different structures
            (a list-price PO alongside a promo PO). Each line is distributed at
            its OWN invoice's factor, so show them rather than only the blend. */}
        {d.perInvoice.length > 1 && (
          <div className="mt-1.5 border-t border-border/40 pt-1.5">
            {d.mixedFactors && (
              <div className={cn('mb-0.5', statusInk('warning'))}>
                These invoices price differently — each product is distributed at its own invoice's factor.
              </div>
            )}
            {d.perInvoice.map((p, i) => (
              <div key={i} className="text-muted-foreground">
                <span className="text-foreground">{p.invoiceNo || '(no number)'}</span>
                {p.chargeOnly ? (
                  <> · charge only ({money(p.total, currency)}), spread across the goods invoices</>
                ) : (
                  <>
                    {' · '}{p.lineCount} products · goods {money(p.productSubtotal, currency)} · paid {money(p.total, currency)}
                    {p.allocatedCharge ? <> · +{money(p.allocatedCharge, currency)} freight allocated</> : null}
                    {p.factor != null && <> · factor <span className="text-foreground tabular-nums">{p.factor.toFixed(6)}</span></>}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The verdict numbers */}
      <div className="my-5 grid grid-cols-4 gap-3 max-[900px]:grid-cols-2">
        <div className="rounded-md border border-border/60 bg-background/60 p-2">
          <div className="text-muted-foreground">Booked now</div>
          <div className="mt-2 text-[20px] tracking-[-.4px] tabular-nums font-medium">{money(t.bookedTotal, currency)}</div>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 p-2">
          <div className="text-muted-foreground">Target after edits</div>
          <div className="mt-2 text-[20px] tracking-[-.4px] tabular-nums font-medium">
            {money(t.targetReceivingTotal, currency)}
            {t.costDeltaTotal !== 0 && (
              // Money direction is not a state: plain, the sign says it.
              <span className="ml-1 block text-[12px] tracking-normal">
                ({t.costDeltaTotal > 0 ? '+' : ''}{money(t.costDeltaTotal, currency)})
              </span>
            )}
          </div>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 p-2">
          <div className="text-muted-foreground">
            <Hint content="Invoiced quantities priced at target must come back to the invoice total. Non-zero = missing products, an uncaptured fee, or a bad subtotal.">
              Distribution gap
            </Hint>
          </div>
          <div className={cn('mt-2 text-[20px] tracking-[-.4px] tabular-nums font-medium', gapOk ? statusInk('success') : t.distributionGap != null && statusInk('danger'))}>
            {t.distributionGap == null ? '—' : money(t.distributionGap, currency)}
            {gapOk && <CheckCircle2 className="ml-1 inline h-3.5 w-3.5" />}
          </div>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 p-2">
          <div className="text-muted-foreground">
            <Hint content="The part of invoice-vs-receiving difference legitimately explained by count discrepancies (shorts/overs).">
              Count variance
            </Hint>
          </div>
          <div className="mt-2 text-[20px] tracking-[-.4px] tabular-nums font-medium">{money(t.countVariance, currency)}</div>
        </div>
      </div>

      {/* Step 1 — counts. This list goes to the warehouse, then the supplier. */}
      {balance.countDiscrepancies.length > 0 && (
        <div>
          <div className="mb-1 font-medium">
            Count discrepancies ({balance.countDiscrepancies.length})
          </div>
          <div className="overflow-x-auto rounded-md border border-border/60">
            <table className="w-full">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left font-normal">Item</th>
                  <th className="px-2 py-1 text-right font-normal">Invoiced</th>
                  <th className="px-2 py-1 text-right font-normal">Received</th>
                  <th className="px-2 py-1 text-left font-normal">Kind</th>
                  <th className="px-2 py-1 text-right font-normal">Value</th>
                  <th className="px-2 py-1 text-left font-normal">Decided</th>
                </tr>
              </thead>
              <tbody>
                {balance.countDiscrepancies.map((c, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="px-2 py-1">
                      <span className="line-clamp-1">{c.desc}</span>
                      <ProductLink pid={c.pid} className="text-muted-foreground">
                        {c.itemnumber || c.vendorSku || (c.pid != null ? `pid ${c.pid}` : '')}
                      </ProductLink>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.qtyInvoiced}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.qtyReceived}</td>
                    <td className="px-2 py-1">
                      <StatusPill>{DISCREPANCY_KIND_LABELS[c.kind] || c.kind}</StatusPill>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{c.valueAtTarget == null ? '—' : money(c.valueAtTarget, currency)}</td>
                    {/* A short is a fact; the disposition is the decision, and it is what
                        decides how the receiving is costed. Undecided blocks costing. */}
                    <td className="px-2 py-1">
                      {c.disposition == null ? (
                        <StatusPill tone="warning">undecided</StatusPill>
                      ) : (
                        <span className={cn(c.dispositionSource === 'inferred' && 'opacity-60')}>
                          {c.disposition}
                          {c.creditAmount != null && ` ${money(c.creditAmount, currency)}`}
                          {c.dispositionSource === 'inferred' && ' (auto)'}
                          {c.disposition === 'credited' && c.resolutionStatus && (
                            <StatusPill tone={RESOLUTION_TONE[c.resolutionStatus] ?? 'neutral'} className="ml-1">
                              {c.resolutionStatus === 'pending' ? 'open' : c.resolutionStatus === 'requested' ? 'requested' : 'landed'}
                            </StatusPill>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Steps 2–3 — the literal edit list for the receiving-edit grid */}
      {balance.costEdits.length > 0 && (
        <div>
          <div className="mb-1 font-medium">Cost edits ({balance.costEdits.length} products, {money(t.costDeltaTotal, currency)} total)</div>
          <div className="overflow-x-auto rounded-md border border-border/60">
            <table className="w-full">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left font-normal">Item</th>
                  <th className="px-2 py-1 text-right font-normal">Qty</th>
                  <th className="px-2 py-1 text-right font-normal">Booked</th>
                  <th className="px-2 py-1 text-right font-normal">Target</th>
                  <th className="px-2 py-1 text-right font-normal">Δ total</th>
                </tr>
              </thead>
              <tbody>
                {edits.map((e, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="px-2 py-1">
                      <span className="line-clamp-1">{e.desc}</span>
                      <ProductLink pid={e.pid} className="text-muted-foreground">
                        {e.itemnumber || e.vendorSku || (e.pid != null ? `pid ${e.pid}` : '')}
                      </ProductLink>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{e.qty}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{unitCost(e.booked)}</td>
                    <td className="px-2 py-1 text-right font-medium tabular-nums">{unitCost(e.target)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {e.deltaLine == null ? '—' : money(e.deltaLine, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {balance.costEdits.length > 12 && (
            <button
              type="button"
              className="mt-1 text-primary hover:underline"
              onClick={() => setShowAllEdits((v) => !v)}
            >
              {showAllEdits ? 'Show fewer' : `Show all ${balance.costEdits.length} edits`}
            </button>
          )}
        </div>
      )}

      {balance.linesMissingUnitCost.length > 0 && (
        <div className="text-muted-foreground">
          {balance.linesMissingUnitCost.length} received product{balance.linesMissingUnitCost.length === 1 ? '' : 's'} without an invoice cost
          {' '}({balance.linesMissingUnitCost[0].reason}{balance.linesMissingUnitCost.length > 1 ? ', …' : ''}).
        </div>
      )}

      {/* Step 5 — reference fields for the receiving header */}
      {(balance.headerIds.invoiceId || balance.headerIds.orderNos.length > 0) && (
        <div className="text-muted-foreground">
          Header refs to set on the receiving:
          {balance.headerIds.invoiceId && <span> invoice # <span className="text-foreground">{balance.headerIds.invoiceId}</span></span>}
          {balance.headerIds.orderNos.length > 0 && <span> · order # <span className="text-foreground">{balance.headerIds.orderNos.join(', ')}</span></span>}
        </div>
      )}

      {t.balanced && (
        <div className={cn('flex items-center gap-1.5', statusInk('success'))}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Ties. Ready to apply and mark balanced.
        </div>
      )}
    </div>
  );
}
